/**
 * Krok 6: Výběr plánu + Výpočet valuace
 * 4 plány: FREE, PRO (6990), AUDIT (4990), KOMPLET (9990)
 * PRO / KOMPLET: kalkulace + expertní report (s auto-retry).
 * Na výsledky se přejde až když je vše hotové.
 */
import React, { useState, useRef, useEffect } from 'react';
import { calculateValuation } from '../api/valuationCalculator';
import { generateAIReport } from '../api/aiReportApi';

const STEPS_FREE = [
  'Zpracováváme vaše čísla...',
  'Vyhodnocujeme klíčové metriky...',
  'Porovnáváme s vaším segmentem...',
  'Připravujeme přehled...',
  'Sestavujeme výsledek...',
];

const STEPS_PRO = [
  ...STEPS_FREE,
  'Generuji expertní výkladový report...',
  'Připravuji pohled investora...',
  'Sestavuji prioritní akční plán...',
  'Analyzuji rizikové faktory a příležitosti...',
  'Finalizuji kompletní PRO zprávu...',
];

const STEPS_AUDIT = [
  ...STEPS_FREE,
  'Připravuji podklady pro audit...',
];

const STEPS_KOMPLET = [
  ...STEPS_FREE,
  'Generuji expertní výkladový report...',
  'Připravuji pohled investora...',
  'Sestavuji prioritní akční plán...',
  'Analyzuji rizikové faktory a příležitosti...',
  'Připravuji podklady pro audit...',
  'Finalizuji kompletní analýzu...',
];

const STEP_MAP = {
  free: STEPS_FREE,
  pro: STEPS_PRO,
  audit: STEPS_AUDIT,
  komplet: STEPS_KOMPLET,
};

/* ── Dark theme card styles ── */
const cardBase = {
  borderRadius: 14,
  padding: '1.5rem 1.25rem',
  display: 'flex', flexDirection: 'column', gap: '0.65rem',
};

const cardFree = {
  ...cardBase,
  border: '1px solid rgba(155, 89, 212, 0.2)',
  background: 'var(--purple-dark)',
};

const cardPro = {
  ...cardBase,
  border: '2px solid var(--purple-bright)',
  background: 'var(--purple-dark)',
  boxShadow: '0 0 30px rgba(155, 89, 212, 0.1)',
};

const cardAudit = {
  ...cardBase,
  border: '1px solid rgba(224, 64, 200, 0.3)',
  background: 'var(--purple-dark)',
};

const cardKomplet = {
  ...cardBase,
  border: '2px solid var(--purple-bright)',
  background: 'var(--purple-dark)',
  boxShadow: '0 0 30px rgba(155, 89, 212, 0.15)',
  position: 'relative',
};

const tierLabel = (color) => ({
  fontSize: '0.75rem',
  color: color,
  textTransform: 'uppercase',
  letterSpacing: '0.15em',
  fontWeight: 600,
});

const cardTitle = {
  fontSize: '1.3rem',
  fontWeight: 400,
  color: 'var(--star-white)',
  fontFamily: "'Source Serif 4', Georgia, serif",
};

const cardList = {
  fontSize: '0.85rem',
  color: 'var(--text-dim)',
  lineHeight: 1.5,
  flex: 1,
};

const listStyle = {
  margin: 0,
  paddingLeft: '1.1rem',
  display: 'flex',
  flexDirection: 'column',
  gap: '0.25rem',
};

const priceStyle = (color) => ({
  fontSize: '1.1rem',
  fontWeight: 800,
  color: color,
});

const btnOutline = (color) => ({
  width: '100%', padding: '0.6rem', borderRadius: 8,
  border: `1px solid ${color}`,
  background: 'transparent',
  color: color,
  fontWeight: 600, fontSize: '0.85rem', cursor: 'pointer',
  letterSpacing: '0.08em', textTransform: 'uppercase',
  transition: 'all 0.3s ease',
  fontFamily: "'Inter', sans-serif",
});

const btnGradient = {
  width: '100%', padding: '0.6rem', borderRadius: 8,
  border: 'none',
  background: 'var(--btn-bg)',
  color: 'white',
  fontWeight: 600, fontSize: '0.85rem', cursor: 'pointer',
  letterSpacing: '0.08em', textTransform: 'uppercase',
  transition: 'all 0.3s ease',
  fontFamily: "'Inter', sans-serif",
};

export function Step5_Calculation({ wizardState, setWizardState, onNext, onBack }) {
  const [phase, setPhase]           = useState('loading');
  const [errorMessage, setErrorMessage] = useState('');
  const [stepText, setStepText]     = useState('');
  const intervalRef = useRef(null);
  const startedRef = useRef(false);

  const run = (selectedPlan) => {
    setPhase('loading');
    setErrorMessage('');
    setWizardState(prev => ({ ...prev, selectedPlan }));

    const steps = STEP_MAP[selectedPlan] || STEPS_FREE;
    let idx = 0;
    setStepText(steps[0]);
    intervalRef.current = setInterval(() => {
      idx = (idx + 1) % steps.length;
      setStepText(steps[idx]);
    }, 700);

    const needsAIReport = selectedPlan === 'pro' || selectedPlan === 'komplet';

    if (!needsAIReport) {
      calculateValuation(wizardState).then(result => {
        clearInterval(intervalRef.current);
        if (result.success) {
          setWizardState(prev => ({ ...prev, valuationResult: result.data }));
          onNext();
        } else {
          setPhase('error');
          setErrorMessage(result.error || 'Neznámá chyba.');
        }
      });
    } else {
      calculateValuation(wizardState).then(calcResult => {
        if (!calcResult.success) {
          clearInterval(intervalRef.current);
          setPhase('error');
          setErrorMessage(calcResult.error || 'Neznámá chyba.');
          return;
        }

        setWizardState(prev => ({ ...prev, valuationResult: calcResult.data }));

        if (calcResult.data?.negative_ebitda) {
          clearInterval(intervalRef.current);
          onNext();
          return;
        }

        const MAX_RETRIES = 5;
        const RETRY_DELAYS = [3000, 6000, 12000, 20000];

        const attemptAIReport = (attempt) => {
          setStepText(
            attempt === 0
              ? 'Generuji expertní výkladový report...'
              : `Expertní report, pokus ${attempt + 1}/${MAX_RETRIES}...`
          );

          generateAIReport(wizardState, 'pro').then(aiResult => {
            if (aiResult.success) {
              clearInterval(intervalRef.current);
              setWizardState(prev => ({
                ...prev,
                aiReport: { tier: 'pro', ...aiResult.data },
              }));
              onNext();
            } else if (attempt + 1 < MAX_RETRIES) {
              const delay = RETRY_DELAYS[attempt] || 20000;
              setStepText(`Report se nepodařilo vygenerovat. Další pokus za ${delay / 1000}s...`);
              setTimeout(() => attemptAIReport(attempt + 1), delay);
            } else {
              clearInterval(intervalRef.current);
              setPhase('error');
              setErrorMessage(
                'Expertní report se nepodařilo vygenerovat ani po 5 pokusech. ' +
                (aiResult.error || 'Zkuste to prosím znovu.')
              );
            }
          });
        };
        attemptAIReport(0);
      });
    }
  };

  // Funnel: po vstupu se rovnou spočítá FREE valuace (placené až na výsledcích)
  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;
    run('free');
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fáze: Chyba
  if (phase === 'error') {
    return (
      <div className="step-container">
        <h2>Výpočet valuace</h2>
        <div className="error-message">
          <strong>Chyba při výpočtu:</strong> {errorMessage}
          <div style={{ marginTop: '0.5rem', fontSize: '0.85rem', color: 'var(--text-dim)' }}>
            Zkuste to prosím za chvíli znovu. Pokud potíže přetrvávají, napište nám přes konzultaci.
          </div>
        </div>
        <div className="button-group">
          <button onClick={onBack} className="btn-secondary">Zpět</button>
          <button onClick={() => run('free')} className="btn-primary">Zkusit znovu</button>
        </div>
      </div>
    );
  }

  // Fáze: Spinner (loading)
  if (phase === 'loading') {
    return (
      <div className="step-container" style={{ textAlign: 'center' }}>
        <h2>Výpočet valuace</h2>
        <p className="step-description">
          Počítáme hodnotu vaší firmy...
        </p>
        <div style={{ margin: '3rem auto', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1.5rem' }}>
          <div style={{
            width: 64, height: 64,
            border: '6px solid rgba(155, 89, 212, 0.15)',
            borderTopColor: 'var(--purple-bright)',
            borderRadius: '50%',
            animation: 'spin 0.9s linear infinite',
          }} />
          <p style={{ color: 'var(--text-dim)', fontSize: '1rem', fontStyle: 'italic' }}>{stepText}</p>
          <div style={{
            padding: '0.75rem 1.25rem',
            background: 'rgba(107, 63, 160, 0.1)',
            border: '1px solid rgba(155, 89, 212, 0.2)',
            borderRadius: 10,
            maxWidth: 420,
            textAlign: 'center',
          }}>
            <span style={{ fontSize: '0.82rem', color: 'var(--text-dim)' }}>
              Generování analýzy může trvat až 5 minut. Prosím neaktualizujte stránku.
            </span>
          </div>
        </div>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  // Fáze: Výběr plánu
  return (
    <div className="step-container">
      <h2>Zvolte typ ocenění</h2>
      <p className="step-description">
        Všechna data jsou připravena. Vyberte variantu a spustíme výpočet.
      </p>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.25rem', marginTop: '1.5rem', marginBottom: '1.25rem' }}>
        {/* FREE */}
        <div style={cardFree}>
          <div style={tierLabel('var(--text-dim)')}>FREE</div>
          <div style={cardTitle}>Základní ocenění</div>
          <div style={cardList}>
            <ul style={listStyle}>
              <li>Odhadovaná tržní hodnota</li>
              <li>Konzervativní / optimistický rozsah</li>
              <li>6 klíčových metrik</li>
            </ul>
          </div>
          <div style={priceStyle('var(--color-success)')}>Zdarma</div>
          <button onClick={() => run('free')} style={btnOutline('var(--purple-light)')}>
            Spustit FREE ocenění
          </button>
        </div>

        {/* PRO */}
        <div style={cardPro}>
          <div style={tierLabel('var(--purple-light)')}>PRO VALUACE</div>
          <div style={cardTitle}>Profesionální valuace</div>
          <div style={cardList}>
            <ul style={listStyle}>
              <li>Vše z FREE varianty</li>
              <li>Finanční zdraví (ROA, ROE, ROS)</li>
              <li>Business Quality Score (8 dimenzí)</li>
              <li>Rizikové faktory + simulace růstu</li>
              <li style={{ color: 'var(--star-white)', fontWeight: 500 }}>Expertní výkladový report</li>
              <li style={{ color: 'var(--star-white)', fontWeight: 500 }}>Pohled investora + Exit Readiness</li>
            </ul>
          </div>
          <div style={priceStyle('var(--purple-light)')}>6 990 Kč</div>
          <button onClick={() => run('pro')} style={btnGradient}>
            Spustit PRO valuaci
          </button>
        </div>
      </div>

      {/* Audit jen tam, kde má s čím srovnávat (viz Step6 auditDostupny). */}
      {[undefined, null, '', 'eshop'].includes(wizardState.typ_podnikani) && (
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.25rem', marginBottom: '2rem' }}>
        {/* AUDIT */}
        <div style={cardAudit}>
          <div style={tierLabel('var(--nebula-pink)')}>AUDIT</div>
          <div style={cardTitle}>Konzultantský audit</div>
          <div style={cardList}>
            <ul style={listStyle}>
              <li>Analýza ztraceného zisku</li>
              <li>6 audit modulů (marže, retence, reklama...)</li>
              <li>Prioritní akce seřazené dle dopadu</li>
              <li style={{ color: 'var(--star-white)', fontWeight: 500 }}>Konzultantský report + SWOT</li>
              <li style={{ color: 'var(--star-white)', fontWeight: 500 }}>Detailní průvodce, krok za krokem</li>
            </ul>
          </div>
          <div style={priceStyle('var(--nebula-pink)')}>4 990 Kč</div>
          <button onClick={() => run('audit')} style={btnOutline('var(--nebula-pink)')}>
            Spustit audit
          </button>
        </div>

        {/* KOMPLET */}
        <div style={cardKomplet}>
          <div style={{
            position: 'absolute', top: -12, right: 16,
            background: 'var(--btn-bg)',
            color: 'white',
            padding: '0.2rem 0.75rem', borderRadius: 999,
            fontSize: '0.72rem', fontWeight: 700,
            letterSpacing: '0.05em',
          }}>
            Ušetříte 1 990 Kč
          </div>
          <div style={tierLabel('var(--purple-light)')}>PRO + AUDIT</div>
          <div style={cardTitle}>Kompletní řešení</div>
          <div style={cardList}>
            <ul style={listStyle}>
              <li style={{ color: 'var(--star-white)', fontWeight: 500 }}>Vše z PRO valuace</li>
              <li style={{ color: 'var(--star-white)', fontWeight: 500 }}>Vše z auditu</li>
              <li>Expertní report + konzultantský audit</li>
              <li>Pohled investora + akční plán</li>
              <li>Kompletní podklady pro exit / investora</li>
            </ul>
          </div>
          <div>
            <span style={priceStyle('var(--purple-light)')}>9 990 Kč</span>
            <span style={{ fontSize: '0.82rem', color: 'rgba(210,195,255,0.4)', marginLeft: '0.5rem', textDecoration: 'line-through' }}>11 980 Kč</span>
          </div>
          <button onClick={() => run('komplet')} style={btnGradient}>
            Spustit kompletní analýzu
          </button>
        </div>
      </div>
      )}

      <div className="button-group">
        <button onClick={onBack} className="btn-secondary">Zpět</button>
      </div>
    </div>
  );
}
